import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BellOffIcon } from 'lucide-react';
import { Button } from '@noor/ui';
import { toast } from '@noor/lib';
import { splitSearchingDuration, type OverdueSearchOrder } from '@/entities/order';
import { AlarmSound } from './alarm-sound';

const ALARM_TOAST_ID = 'search-alarm';

type OrderKey = OverdueSearchOrder['id'];

/**
 * Full-page night alarm (cpanel `SearchAlarmOverlay`). Renders nothing until
 * an un-acknowledged order is overdue; then it pulses a danger-red vignette
 * over the whole UI (the page stays usable underneath), loops the siren, and
 * keeps one sticky toast naming how many orders are stuck and the worst wait.
 *
 * Dismissal is by order IDENTITY: stopping (the button, or closing the toast)
 * acknowledges the orders overdue right now; they never re-alarm however long
 * they wait. Only a new id crossing the threshold re-arms it. Acknowledgements
 * for orders that left the overdue set are pruned, so an order that goes back
 * into search can alarm again later.
 */
export function SearchAlarmOverlay({ overdue }: { overdue: OverdueSearchOrder[] }) {
  const { t } = useTranslation('orders');
  const [acknowledged, setAcknowledged] = useState<ReadonlySet<OrderKey>>(
    () => new Set(),
  );

  // Prune while rendering (no effect): keep only ids that are still overdue.
  const overdueIds = useMemo(() => new Set(overdue.map((o) => o.id)), [overdue]);
  if ([...acknowledged].some((id) => !overdueIds.has(id))) {
    setAcknowledged(new Set([...acknowledged].filter((id) => overdueIds.has(id))));
  }

  const active = overdue.filter((o) => !acknowledged.has(o.id));
  const isActive = active.length > 0;
  const count = active.length;
  const worst = active[0];

  const dismiss = () => setAcknowledged(new Set(overdue.map((o) => o.id)));

  const sirenRef = useRef<AlarmSound | null>(null);
  useEffect(() => {
    const siren = new AlarmSound();
    sirenRef.current = siren;
    return () => siren.dispose();
  }, []);

  useEffect(() => {
    if (isActive) sirenRef.current?.start();
    else sirenRef.current?.stop();
  }, [isActive]);

  // Closing the toast acknowledges, exactly like the on-screen button. Read
  // through a ref so the toast's callback always sees the latest overdue set.
  const dismissRef = useRef(dismiss);
  useEffect(() => {
    dismissRef.current = dismiss;
  });

  const worstId = worst?.displayId;
  const worstMs = worst?.searchingMs;
  useEffect(() => {
    if (!isActive || worstId === undefined || worstMs === undefined) {
      toast.dismiss(ALARM_TOAST_ID);
      return;
    }
    const duration = splitSearchingDuration(worstMs);
    // cpanel's wording switches only between one and many ("заказ ищет" /
    // "заказов ищут"), not the full Russian plural set.
    toast.sticky(
      t(count === 1 ? 'alarm.toastOne' : 'alarm.toastMany', {
        total: count,
        number: worstId,
        minutes: duration.minutes,
        seconds: duration.seconds,
      }),
      { id: ALARM_TOAST_ID, onDismiss: () => dismissRef.current() },
    );
  }, [isActive, count, worstId, worstMs, t]);

  useEffect(() => () => void toast.dismiss(ALARM_TOAST_ID), []);

  if (!isActive) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-[2147483646]"
    >
      <div className="search-alarm-bleed absolute inset-0" />
      <div className="search-alarm-ring absolute inset-0" />
      <div className="absolute inset-x-0 bottom-8 flex justify-center">
        <Button
          variant="destructive"
          shape="rounded"
          size="modal"
          className="pointer-events-auto px-8 font-bold shadow-xl"
          onClick={dismiss}
        >
          <BellOffIcon className="size-4" />
          {t('alarm.stop')}
        </Button>
      </div>
    </div>
  );
}
